import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { titleCase, upperCase, formatDateTime, formatDateOnly } from '../../../lib/utils';
import { useBreadcrumbs } from '../../../app/breadcrumb-provider';
import { TrackingDataPTL } from '../../../types/tracking';
import OrderDetails from './OrderDetails';
import { generateShareLink } from 'features/shipments/api';
import { Button } from 'components/ui/button';
import ShareLinkModal from 'components/ui/share-link-modal';
import { useMediaQuery } from '../../../hooks/use-mobile';

interface TrackingPagePTLProps {
    data: TrackingDataPTL;
    isPublic?: boolean;
}

const MilestoneStepper: React.FC<{ milestones: TrackingDataPTL['milestones'] }> = ({ milestones }) => {
    return (
        <div className="w-full py-4 sm:py-6 overflow-x-auto no-scrollbar">
            <div className="flex items-start justify-between min-w-[320px] px-2 sm:px-4">
                {milestones.map((milestone, index) => {
                    const isCompleted = milestone.completed;
                    const isCurrent = milestone.current;
                    const isLast = index === milestones.length - 1;

                    const nextMilestone = milestones[index + 1];
                    const lineToNextGreen = nextMilestone && (nextMilestone.completed || nextMilestone.current);

                    return (
                        <React.Fragment key={milestone.id}>
                            <div className="flex flex-col items-center shrink-0">
                                <div className="w-12 h-12 flex items-center justify-center z-10">
                                    {isCurrent ? (
                                        <img
                                            src="/images/truck.png"
                                            alt="Truck"
                                            className="w-full h-full object-contain select-none"
                                            draggable={false}
                                        />
                                    ) : (
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}`}>
                                            {isCompleted && (
                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className={`mt-2 text-xs sm:text-sm font-semibold whitespace-nowrap text-center ${isCurrent || isCompleted ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {milestone.label}
                                </div>
                            </div>

                            {!isLast && (
                                <div className="flex-1 flex items-center px-1 sm:px-2" style={{ paddingTop: '24px' }}>
                                    <div className={`h-1 w-full rounded-full ${lineToNextGreen ? 'bg-green-500' : 'bg-gray-300'}`} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

const TrackingPagePTL: React.FC<TrackingPagePTLProps> = ({ data, isPublic }) => {
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const { setRightHeaderActions } = useBreadcrumbs();
    const cnNumber = data.cn_number;
    const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
    const [shareUrl, setShareUrl] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const handleShareClick = useCallback(async () => {
        if (!cnNumber) return;
        try {
            const response = await generateShareLink(cnNumber);
            const fullUrl = `${window.location.origin}/shipments/track/public?token=${response.share_token}`;
            setShareUrl(fullUrl);
            setIsShareModalOpen(true);
        } catch (err) {
            setError("Failed to generate share link.");
        }
    }, [cnNumber]);

    useEffect(() => {
        if (!isPublic) {
            setRightHeaderActions(
                <div className="hidden lg:block">
                    <Button variant="outline" size="sm" onClick={handleShareClick} className="bg-white border-gray-200 text-xs px-3 h-8 shadow-sm hover:bg-gray-50">
                        <span className="mr-1.5">🔗</span> Share Link
                    </Button>
                </div>
            );
        }

        return () => {
            setRightHeaderActions(null);
        };
    }, [setRightHeaderActions, isPublic, handleShareClick]);

    const lastUpdatedFormatted = formatDateTime(data.last_updated);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(shareUrl);
        setIsShareModalOpen(false);
    };

    const isOnHold = data.status === 'on_hold';
    const isAwaitingPickup = data.status === 'awaiting_pickup';
    const stockDays = (isOnHold || isAwaitingPickup) && data.stock_since
        ? Math.floor((new Date().getTime() - new Date(data.stock_since).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

    // Helper render functions
    const renderPublicBanner = (isDesktopView: boolean) => isPublic ? (
        <div className={isDesktopView ? "mb-6 text-left" : "mb-3.5 text-center"}>
            <img src="/logo.png" alt="Logo" className={isDesktopView ? "h-10 mb-4" : "h-6 sm:h-7 mb-1.5 mx-auto"} />
            <h2 className={isDesktopView ? "text-xl font-bold text-gray-800" : "text-sm sm:text-base font-bold text-gray-800 tracking-tight"}>Shipment Tracking</h2>
            <p className={isDesktopView ? "text-sm text-gray-500" : "text-xs text-gray-500 mt-0.5"}>
                CN Number: <span className="font-semibold text-gray-700">{data.cn_number}</span>
            </p>
        </div>
    ) : null;

    const renderDesktopHeader = () => (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:justify-between gap-8">
                <div className="flex-1">
                    <div className="text-gray-500 text-sm mb-1">From</div>
                    <div className="text-lg font-bold text-gray-900 mb-1">{upperCase(data.consignor)}</div>
                    <div className="text-sm text-gray-600">left {titleCase(data.origin)}</div>
                </div>

                <div className="flex-1 md:text-right">
                    <div className="text-gray-500 text-sm mb-1">Expected Delivery Date</div>
                    <div className="text-lg font-bold text-gray-900 mb-1">{formatDateTime(data.eta).date}</div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row md:justify-between gap-8">
                <div className="flex-1">
                    <div className="text-gray-500 text-sm mb-1">To</div>
                    <div className="text-lg font-bold text-gray-900 mb-1">{upperCase(data.consignee)}</div>
                    <div className="text-sm text-gray-600">arriving at {titleCase(data.destination)}</div>
                </div>
            </div>
        </div>
    );

    const renderFromToGrid = () => (
        <div className="grid grid-cols-2 gap-4">
            <div className="text-left min-w-0">
                <div className="text-gray-500 text-xs mb-1">From</div>
                <div className="text-base sm:text-lg font-bold text-gray-900 leading-snug break-words mb-1">
                    {upperCase(data.consignor)}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                    left {titleCase(data.origin)}
                </div>
            </div>

            <div className="text-right min-w-0">
                <div className="text-gray-500 text-xs mb-1">To</div>
                <div className="text-base sm:text-lg font-bold text-gray-900 leading-snug break-words mb-1">
                    {upperCase(data.consignee)}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 mb-0.5">
                    arriving at {titleCase(data.destination)}
                </div>
                <div className="text-xs font-medium text-gray-700">
                    ETA: {formatDateTime(data.eta).date}
                </div>
            </div>
        </div>
    );

    const renderStatusBox = () => (
        <div className={`p-4 rounded-xl border ${(isOnHold || isAwaitingPickup)
            ? 'bg-orange-50/80 border-orange-200'
            : 'bg-blue-50/80 border-blue-200'
            }`}>
            <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full animate-pulse shrink-0 ${(isOnHold || isAwaitingPickup) ? 'bg-orange-500' : 'bg-blue-500'}`}></div>
                <span className={`text-sm font-medium ${(isOnHold || isAwaitingPickup) ? 'text-orange-900' : 'text-blue-900'}`}>
                    {isAwaitingPickup
                        ? `Awaiting Pickup at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                        : isOnHold
                        ? `Currently on hold at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                        : `Currently in ${data.current_address}`
                    }
                </span>
            </div>
            <div className={`text-xs mt-1 pl-4 ${(isOnHold || isAwaitingPickup) ? 'text-orange-700' : 'text-blue-700'}`}>
                Last updated: {lastUpdatedFormatted.date} {lastUpdatedFormatted.time}
            </div>
        </div>
    );

    const renderProgressCard = () => (
        <Card className="bg-white shadow-sm border border-gray-100">
            <CardContent className="p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Shipment Progress</h3>
                <div className="space-y-0">
                    {data.progress.map((item, idx) => (
                        <div key={idx} className="flex items-center py-2.5 sm:py-3 border-b border-gray-100 last:border-b-0">
                            <div className="min-w-[110px] sm:min-w-[140px] text-xs sm:text-sm font-semibold text-gray-900 flex-shrink-0">
                                {formatDateTime(item.timestamp).date}
                            </div>
                            <div className="text-gray-300 mx-2 sm:mx-4 flex-shrink-0">|</div>
                            <div className="text-xs sm:text-sm text-gray-600 flex-1">
                                {item.update}
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );

    return (
        <div className="h-full flex flex-col relative overflow-hidden -mx-4 -mt-4 -mb-4 lg:m-0">
            {isDesktop ? (
                <div className="h-full w-full overflow-y-auto bg-white p-6 relative">
                    <div className="max-w-4xl mx-auto space-y-6">
                        {error && (
                            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">
                                {error}
                            </div>
                        )}
                        {renderPublicBanner(true)}

                        {/* 1. Original Desktop Header: From, Expected Delivery Date, To */}
                        {renderDesktopHeader()}

                        {/* 2. Milestone Stepper (No background or padding on desktop) */}
                        <div className="mt-4">
                            <MilestoneStepper milestones={data.milestones} />
                        </div>

                        {/* 3. Current Location Status Box */}
                        {renderStatusBox()}

                        {/* 4. Shipment Progress */}
                        {renderProgressCard()}

                        {/* 5. Order Details */}
                        <OrderDetails orderDetails={data.order_details} />
                    </div>
                </div>
            ) : (
                <div className="h-full flex flex-col overflow-y-auto relative bg-gray-50/50">
                    {!isPublic && (
                        <div className="absolute top-1.5 right-2 sm:top-2 sm:right-3 z-30">
                            <Button variant="outline" size="sm" onClick={handleShareClick} className="bg-white/95 backdrop-blur shadow-sm border-gray-200 text-xs px-2.5 h-7 sm:h-8">
                                <span className="mr-1.5">🔗</span> Share
                            </Button>
                        </div>
                    )}

                    <div className="flex-1 min-h-0 flex flex-col gap-0 p-0 relative">
                        {/* Sticky Hero Milestone Stepper Header */}
                        <div className="w-full min-h-[200px] sm:min-h-[240px] sticky top-0 z-0 flex flex-col justify-center items-center bg-gradient-to-b from-slate-100/90 via-blue-50/40 to-white p-4 pt-6 pb-10 shrink-0">
                            <div className="w-full max-w-lg mx-auto">
                                <div className="text-xs uppercase tracking-wider font-bold text-gray-400 mb-2 text-center">
                                    Shipment Milestone Journey
                                </div>
                                <MilestoneStepper milestones={data.milestones} />
                            </div>
                        </div>

                        {/* Bottom Sheet Modal */}
                        <div className="w-full relative z-10 -mt-6 bg-white rounded-t-[28px] shadow-[0_-8px_30px_rgba(0,0,0,0.12)] border-t border-gray-100 px-5 pt-3 pb-10 flex flex-col min-h-[55vh] space-y-5">
                            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-2 shrink-0" />

                            {error && (
                                <div className="mb-2 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">
                                    {error}
                                </div>
                            )}

                            {renderPublicBanner(false)}
                            {renderFromToGrid()}
                            {renderStatusBox()}
                            {renderProgressCard()}
                            <div>
                                <OrderDetails orderDetails={data.order_details} className="flex flex-col" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ShareLinkModal
                isOpen={isShareModalOpen}
                shareUrl={shareUrl}
                onCopy={handleCopy}
                onClose={() => setIsShareModalOpen(false)}
            />
        </div>
    );
};

export default TrackingPagePTL;
